<script>
  import { createEventDispatcher, onDestroy, onMount } from "svelte";
  import { validateAspectRatioClasses } from "../utilities/aspectRatios";
  import { v4 as uuid } from "@lukeed/uuid";

  const dispatch = createEventDispatcher();

  /** Primarily used for testing @type {string} */
  export let id = undefined;

  /**
   * Indicates whether the video should start with autoplay config.
   * @type {boolean}
   */
  export let shouldAutoPlay = false;

  /**
   * A string of space-separated aspect ratio Tailwind classes.
   * @type {string}
   * @default "aspect-16/9"
   */
  export let aspectRatioClasses = "aspect-16/9";
  validateAspectRatioClasses(aspectRatioClasses);

  /**
   *
   * @type {boolean} isPlaying - Indicates whether the video is currently playing.
   * @type {boolean} isMuted - Indicates whether the video is currently muted.
   */
  export let isPlaying = false;
  let isMuted;

  /**
   *
   * @type {boolean} shouldShowControls - Indicates if the video should show the controls.
   */
  export let shouldShowControls = false;

  /**
   * Loop section of the video
   *
   * @param {Object} loopSection
   * @param {number} loopSection.startTime - The start time of the loop section in seconds.
   * @param {number} loopSection.endTime - The end time of the loop section in seconds.
   */
  export let loopSection = {
    startTime: 0,
    endTime: 0
  };

  /**
   * The ID of the YouTube video to embed.
   * @type {string}
   */
  export let videoId;

  let divId = `player_${uuid()}`;
  /**
   * Determines whether the YouTube iframe should be muted.
   * If `shouldAutoPlay` is true or both `loopSection.startTime` and `loopSection.endTime` are greater than 0,
   * the iframe should be muted.
   * @type {number}
   */
  const shouldMute =
    shouldAutoPlay || (loopSection.startTime >= 0 && loopSection.endTime > 0) ? 1 : 0;

  /**
   * The player variable represents the YouTube iframe player.
   */
  let player;

  let playerVars = {
    rel: 0,
    controls: 0,
    autoplay: shouldAutoPlay || (loopSection.startTime >= 0 && loopSection.endTime > 0) ? 1 : 0,
    mute: shouldMute
  };

  // Watch for changes in props and reload the iframe
  $: {
    if (player && shouldShowControls) {
      loopSection = undefined;
      player.destroy(); // Destroy existing player
      clearTimeout(timer);
      playerVars = {
        rel: 0,
        controls: 1,
        autoplay: 1,
        mute: 1
      };
      loadPlayer(); // Reload player with new props
    }
  }

  const loadPlayer = () => {
    if (typeof window?.YT !== "undefined") {
      /* eslint-disable no-undef */
      window.YT.ready(() => {
        player = new YT.Player(divId, {
          width: "100%",
          height: "100%",
          videoId,
          playerVars,
          events: {
            onReady: onPlayerReady,
            onStateChange: onPlayerStateChange
          }
        });
      });
    }
  };

  onMount(() => {
    if (typeof window !== "undefined") {
      if (typeof window.YT !== "undefined") {
        loadPlayer();
      } else {
        window.onYouTubeIframeAPIReady = loadPlayer();
      }
    }
  });

  onDestroy(() => {
    if (player) {
      player.destroy();
    }
  });

  const onPlayerReady = () => {
    if (loopSection && loopSection.startTime >= 0 && loopSection.endTime > 0) {
      player.seekTo(loopSection.startTime);
      isPlaying = true;
    }
  };

  let timer = 0;
  const onPlayerStateChange = ({ data }) => {
    if (data === YT.PlayerState.PLAYING) {
      isPlaying = true;

      if (
        !shouldShowControls &&
        loopSection &&
        loopSection.startTime >= 0 &&
        loopSection.endTime > 0
      ) {
        const duration = loopSection.endTime - loopSection.startTime;
        timer = setTimeout(restartVideoSection, duration * 1000);
      }
    } else {
      isPlaying = false;
    }
  };

  export const restartVideoSection = () => {
    if (player?.seekTo) {
      player.seekTo(loopSection.startTime);
    }
  };

  export const startVideoFromBeginning = () => {
    if (player?.seekTo) {
      player.seekTo(0);
    }
  };

  export const stopVideo = () => {
    player?.stopVideo();
  };

  export const pauseVideo = () => {
    player?.pauseVideo();
  };

  export const playVideo = () => {
    player?.playVideo();
  };

  export const toggleMute = () => {
    if (isMuted || player.isMuted()) {
      player.unMute();
      isMuted = false;
    } else {
      player.mute();
      isMuted = true;
    }
  };

  export const togglePause = () => {
    if (isPlaying) {
      player.pauseVideo();
    } else {
      player.playVideo();
    }

    dispatch("togglePause");
  };
</script>

<div {id} class="{aspectRatioClasses} relative">
  <div
    id={divId}
    class="absolute left-0 top-0 z-[--stack-level-youtube-iframe] h-full w-full"
    data-chromatic="ignore"
  />
  <div class="absolute left-0 top-0 h-full w-full bg-black" />
</div>
